import CallRounded from '@mui/icons-material/CallRounded';
import EmailRounded from '@mui/icons-material/EmailRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useSupportContact } from '../api';
import { contactLinks, type SupportContact } from '../lib';
import { IconTile } from '../ui';

const ICONS: Record<ReturnType<typeof contactLinks>[number]['kind'], ReactNode> = {
  phone: <CallRounded />,
  whatsapp: <WhatsApp />,
  email: <EmailRounded />,
  website: <LanguageRounded />,
};

const KIND_LABEL = { phone: 'Call', whatsapp: 'WhatsApp', email: 'Email', website: 'Website' };

/** PackBytes phone, WhatsApp, email and website as tap-to-contact rows. */
export function ContactList({ contact }: { contact: SupportContact }) {
  return (
    <Stack spacing={1}>
      {contactLinks(contact).map((l) => (
        <Box
          key={l.kind}
          component="a"
          href={l.href}
          target={l.kind === 'phone' || l.kind === 'email' ? undefined : '_blank'}
          rel="noreferrer"
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${(t.vars ?? t).palette.divider}`,
            color: 'text.primary',
            textDecoration: 'none',
            '&:hover': { bgcolor: 'action.hover' },
          })}
        >
          <IconTile size={36} color={l.kind === 'whatsapp' ? 'success' : 'primary'}>
            {ICONS[l.kind]}
          </IconTile>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {KIND_LABEL[l.kind]}
            </Typography>
            <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{l.label}</Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/** "Contact PackBytes" card for the subscription page and the locked screen. */
export function ContactCard() {
  const contact = useSupportContact();
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h5">Contact PackBytes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          To subscribe or renew, reach us on any of these. We will send you an activation code.
        </Typography>
        {contact.data ? (
          <ContactList contact={contact.data} />
        ) : (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={58} />
            <Skeleton variant="rounded" height={58} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
